/**
 * `ViewSlide` — cambio de VISTA con deslizamiento direccional.
 *
 * Corre en el row-drill modo SWAP (`row_drill_mode: "swap"`, el
 * DEFAULT): la tabla se reemplaza por el panel de detalle en la misma
 * celda. Ese es el diseño de 2026-07 (PR #54). El 2026-09-09 Pablo pidió
 * además un acordeón y, tras una reunión ese mismo día, decidió tener
 * los DOS modos y elegir por reporte: el swap sigue siendo el default y
 * conserva este deslizamiento; el acordeón (`row_drill_mode:
 * "accordion"`) es opt-in por reporte, despliega el detalle bajo la
 * fila sin cambio de vista y por eso NO usa `ViewSlide`. El problema
 * que resuelve acá es el de siempre: el reemplazo pasaba de golpe —
 * `display:none` en una cara, la otra montada — así que nada le decía
 * al lector que había cambiado de vista, ni en qué dirección.
 *
 * Acá la cara que se va sale hacia un lado y la que llega entra desde
 * el otro; "Volver" corre el MISMO gesto invertido. La dirección es el
 * mensaje: izquierda es un nivel mas adentro, derecha es de vuelta.
 *
 * Dos cosas que no son cosméticas:
 *
 *  * **Las dos caras recorren el 100% a la misma velocidad**, así que
 *    en cada frame se tocan borde a borde. Si una se moviera menos que
 *    la otra, el fondo de la tarjeta se vería por el medio.
 *  * **La cara saliente es un SNAPSHOT**, con la misma mecánica de
 *    `FadeSwap`: se captura en fase de render, en el MISMO commit que
 *    el cambio. Diferirlo (un `useEffect`, un `queueMicrotask`) pinta
 *    un frame del destino sin la saliente encima, que es el parpadeo
 *    "nuevo → viejo → nuevo" que `FadeSwap` documenta.
 *
 * Lo que este componente NO hace: desmontar la cara de la tabla. El
 * caller la mantiene montada y oculta para que "Volver" sea instantáneo
 * — sin refetch, con página, orden y búsqueda intactos. Este componente
 * sólo anima el cruce.
 *
 * `prefers-reduced-motion`: cambio instantáneo, sin desplazamiento.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

import {
  SuppressEntrance,
  MOTION,
  ensureMotionStyles,
  usePrefersReducedMotion,
} from "@pervasivemind/bi-components";

export interface ViewSlideProps {
  /** Identidad de la vista mostrada. Al cambiar, se anima el cruce. */
  viewKey: string | number;
  /** `forward` = un nivel mas adentro (sale a la izquierda).
   *  `back` = de vuelta (sale a la derecha). Es el mismo gesto en espejo. */
  direction: "forward" | "back";
  children: ReactNode;
  /** Duración en ms. Por defecto `MOTION.durView`. */
  durationMs?: number;
  className?: string;
  /** Clases de la cara ENTRANTE — usa `h-full` cuando el hijo depende
   *  de una cadena de alturas porcentuales (celdas estiradas por la
   *  grilla). */
  innerClassName?: string;
  /** Sin animación: la vista cambia de golpe. */
  disabled?: boolean;
}

interface Outgoing {
  key: string | number;
  node: ReactNode;
  direction: "forward" | "back";
}

export function ViewSlide({
  viewKey,
  direction,
  children,
  durationMs = MOTION.durView,
  className,
  innerClassName,
  disabled = false,
}: ViewSlideProps): JSX.Element {
  ensureMotionStyles();
  const reduced = usePrefersReducedMotion();

  const lastKeyRef = useRef<string | number>(viewKey);
  const lastNodeRef = useRef<ReactNode>(children);
  const [outgoing, setOutgoing] = useState<Outgoing | null>(null);
  // Sube en cada cruce para que dos cambios seguidos remonten la cara
  // saliente y su animación arranque de nuevo desde el borde.
  const [swapCount, setSwapCount] = useState(0);

  if (lastKeyRef.current !== viewKey) {
    // Actualización de estado en fase de render (el patrón oficial de
    // React): re-renderea sincrónicamente antes de commitear, así la
    // cara saliente monta en el MISMO commit que la entrante. El ref
    // se actualiza primero, así que el re-render no vuelve a entrar
    // acá — y bajo StrictMode el render doble ya ve el ref al día, o
    // sea que el snapshot se toma exactamente una vez.
    const snapshot: Outgoing = {
      key: lastKeyRef.current,
      node: lastNodeRef.current,
      direction,
    };
    lastKeyRef.current = viewKey;
    if (!reduced && !disabled) {
      setOutgoing(snapshot);
      setSwapCount((c) => c + 1);
    }
  }
  lastNodeRef.current = children;

  useEffect(() => {
    if (outgoing === null) return undefined;
    const t = setTimeout(() => setOutgoing(null), durationMs);
    return () => clearTimeout(t);
  }, [outgoing, durationMs]);

  const animating = outgoing !== null && !reduced && !disabled;
  const forward = outgoing?.direction === "forward";

  return (
    <div
      // `overflow-hidden` SOLO mientras cruza: dejarlo fijo recortaría
      // los popovers de filtros y el menú de exportar de la tabla, que
      // se desbordan de la celda a propósito.
      className={`relative${animating ? " overflow-hidden" : ""}${
        className ? ` ${className}` : ""
      }`}
      data-pm-viewslide
      data-pm-viewslide-animating={animating ? "" : undefined}
    >
      <div
        className={innerClassName}
        style={
          animating
            ? {
                animation: `${forward ? "pmViewInRight" : "pmViewInLeft"} ${durationMs}ms ${MOTION.easeOut} forwards`,
              }
            : undefined
        }
      >
        {children}
      </div>
      {animating && (
        <div
          key={`out-${String(outgoing.key)}-${swapCount}`}
          aria-hidden="true"
          data-pm-viewslide-outgoing
          className="pointer-events-none absolute inset-0"
          style={{
            animation: `${forward ? "pmViewOutLeft" : "pmViewOutRight"} ${durationMs}ms ${MOTION.easeOut} forwards`,
          }}
        >
          {/* Igual que en `FadeSwap`: la saliente es un montaje NUEVO
              del subárbol viejo, así que sin suprimir las entradas sus
              animaciones de "de cero al valor" arrancarían otra vez,
              contando hacia datos que ya no están, encima de la vista
              en vivo. */}
          <SuppressEntrance>{outgoing.node}</SuppressEntrance>
        </div>
      )}
    </div>
  );
}
